import React from 'react';
import BaseQuestion from './BaseQuestion';
import { QUESTION_CONFIGS } from '../../constants/question-types';

/**
 * Choice-based question component (Multiple Choice, Checkbox, Dropdown)
 */
const ChoiceQuestion = ({
  question,
  questionIndex,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  questionType,
}) => {
  const config = QUESTION_CONFIGS[questionType];
  const options = question.config?.options || [];
  const maxOptions = config.limits.maxOptions;

  const [isBulkAdd, setIsBulkAdd] = React.useState(false);
  const [bulkText, setBulkText] = React.useState('');

  const handleOptionChange = (optionIndex, value) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex] = value;
    const updatedConfig = {
      ...question.config,
      options: updatedOptions,
    };
    onUpdate(questionIndex, { config: updatedConfig });
  };

  const addOption = () => {
    if (options.length >= maxOptions) return;
    const updatedOptions = [...options, ''];
    const updatedConfig = {
      ...question.config,
      options: updatedOptions,
      // For checkbox, update maxSelections when adding options
      ...(questionType === 'CHECKBOX' && { maxSelections: Math.min(updatedOptions.length, question.config?.maxSelections || updatedOptions.length) }),
    };
    onUpdate(questionIndex, { config: updatedConfig });
  };

  const handleBulkAdd = () => {
    if (!bulkText.trim()) {
      setIsBulkAdd(false);
      return;
    }

    const newOptions = bulkText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // Remove empty options from existing list (so bulk add replaces empty defaults)
    const nonEmptyExistingOptions = options.filter(opt => opt.trim().length > 0);

    // Calculate available slots based on non-empty options
    const availableSlots = maxOptions - nonEmptyExistingOptions.length;
    const optionsToAdd = newOptions.slice(0, availableSlots);

    if (optionsToAdd.length === 0) {
      setIsBulkAdd(false);
      setBulkText('');
      return;
    }

    const updatedOptions = [...nonEmptyExistingOptions, ...optionsToAdd];
    const updatedConfig = {
      ...question.config,
      options: updatedOptions,
      ...(questionType === 'CHECKBOX' && { maxSelections: Math.min(updatedOptions.length, question.config?.maxSelections || updatedOptions.length) }),
    };

    onUpdate(questionIndex, { config: updatedConfig });
    setIsBulkAdd(false);
    setBulkText('');
  };

  const removeOption = (optionIndex) => {
    if (options.length <= 1) return; // Keep at least one option
    const updatedOptions = options.filter((_, index) => index !== optionIndex);
    let updatedConfig = {
      ...question.config,
      options: updatedOptions,
    };

    // For checkbox, adjust selections when removing options
    if (questionType === 'CHECKBOX') {
      const currentMaxSelections = question.config?.maxSelections || options.length;
      const currentMinSelections = question.config?.minSelections || 1;

      // Adjust maxSelections to not exceed new options length
      const newMaxSelections = Math.min(updatedOptions.length, currentMaxSelections);

      // Adjust minSelections if it's now greater than maxSelections
      const newMinSelections = Math.min(currentMinSelections, newMaxSelections);

      updatedConfig = {
        ...updatedConfig,
        maxSelections: newMaxSelections,
        minSelections: newMinSelections,
      };
    }

    onUpdate(questionIndex, { config: updatedConfig });
  };

  const handleMaxSelectionsChange = (e) => {
    const currentMinSelections = question.config?.minSelections || 1;
    const maxSelections = e.target.value === '' ? options.length : Math.max(currentMinSelections, Math.min(parseInt(e.target.value), options.length));
    const updatedConfig = {
      ...question.config,
      maxSelections,
    };
    onUpdate(questionIndex, { config: updatedConfig });
  };

  const handleMinSelectionsChange = (e) => {
    const currentMaxSelections = question.config?.maxSelections || options.length;
    const minSelections = Math.max(1, Math.min(parseInt(e.target.value) || 1, currentMaxSelections));
    const updatedConfig = {
      ...question.config,
      minSelections: minSelections,
    };
    onUpdate(questionIndex, { config: updatedConfig });
  };

  const renderOptionInput = (option, index) => {
    const isMultipleChoice = questionType === 'MULTIPLE_CHOICE';
    const isCheckbox = questionType === 'CHECKBOX';
    const isDropdown = questionType === 'DROPDOWN';

    return (
      <div key={index} className="mb-2">
        <div className="d-flex align-items-center gap-2">
          {isMultipleChoice && <span className="text-muted">○</span>}
          {isCheckbox && <span className="text-muted">☐</span>}
          {isDropdown && <span className="text-muted fw-bold">{index + 1}.</span>}
          <input
            type="text"
            value={option}
            onChange={(e) => handleOptionChange(index, e.target.value)}
            placeholder={`Option ${index + 1}`}
            className="form-control flex-grow-1"
          />
          <button
            type="button"
            onClick={() => removeOption(index)}
            disabled={options.length <= 1}
            className="btn btn-sm btn-outline-danger"
            title="Remove option"
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  const getQuestionTypeInfo = () => {
    switch (questionType) {
      case 'MULTIPLE_CHOICE':
        return { label: 'Multiple choice', icon: '🔘' };
      case 'CHECKBOX':
        return { label: 'Checkboxes', icon: '☑️' };
      case 'DROPDOWN':
        return { label: 'Dropdown', icon: '📋' };
      default:
        return { label: questionType, icon: '❓' };
    }
  };

  const { label: typeLabel, icon: typeIcon } = getQuestionTypeInfo();

  return (
    <>
      <BaseQuestion
        question={question}
        questionIndex={questionIndex}
        onUpdate={onUpdate}
        onRemove={onRemove}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        isFirst={isFirst}
        isLast={isLast}
        questionType={questionType}
        questionTypeLabel={typeLabel}
        questionTypeIcon={typeIcon}
      >
        <div className="mt-3">
          {/* Options */}
          <div className="mb-3">
            {options.map((option, index) => renderOptionInput(option, index))}
          </div>

          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              onClick={addOption}
              disabled={options.length >= maxOptions}
              className="btn btn-outline-primary btn-sm"
            >
              + Add Option {options.length >= maxOptions && `(Max: ${maxOptions})`}
            </button>
            <button
              type="button"
              onClick={() => setIsBulkAdd(true)}
              disabled={options.length >= maxOptions}
              className="btn btn-outline-secondary btn-sm"
            >
              📋 Bulk Add
            </button>
          </div>

          {/* Selection limits only for checkbox */}
          {questionType === 'CHECKBOX' && (
            <div className="p-3 bg-light rounded border">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Min selections:
                    <input
                      type="number"
                      min="1"
                      max={question.config?.maxSelections || options.length}
                      value={question.config?.minSelections || 1}
                      onChange={handleMinSelectionsChange}
                      className="form-control form-control-sm"
                    />
                  </label>
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Max selections:
                    <input
                      type="number"
                      min={question.config?.minSelections || 1}
                      max={options.length}
                      value={question.config?.maxSelections || options.length}
                      onChange={handleMaxSelectionsChange}
                      className="form-control form-control-sm"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </BaseQuestion>

      {/* Bulk Add Modal */}
      {isBulkAdd && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📋 Bulk Add Options</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setIsBulkAdd(false);
                    setBulkText('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <label className="form-label">
                  Paste your options below (one per line):
                </label>
                <textarea
                  className="form-control"
                  rows="8"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder="Option 1&#10;Option 2&#10;Option 3&#10;..."
                  autoFocus
                ></textarea>
                <div className="form-text mt-2">
                  <small>
                    • Empty lines will be ignored<br />
                    • Existing empty options will be replaced<br />
                    • Maximum {maxOptions} options allowed
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsBulkAdd(false);
                    setBulkText('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBulkAdd}
                  disabled={!bulkText.trim()}
                >
                  Add Options
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChoiceQuestion;
